import React, { useState, useEffect } from 'react';
import './Grafico.css';
import { PolarArea } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    RadialLinearScale,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import baseURL from '../../url';
import useSafeDataFetch from '../../../hooks/useSafeDataFetch';

ChartJS.register(
    CategoryScale,
    LinearScale,
    RadialLinearScale,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

export default function Grafico2() {
    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});

    // Usar el hook seguro para cargar datos
    const { data: rifasData, loading: rifasLoading, error: rifasError } = useSafeDataFetch(`${baseURL}/rifaGet.php`);
    const { data: numerosData, loading: numerosLoading, error: numerosError } = useSafeDataFetch(`${baseURL}/numeroGet.php`);

    const rifas = rifasData?.rifas || [];
    const numeros = numerosData?.numeros || [];


    // Procesar datos para el gráfico
    const procesarDatosGrafico = () => {
        try {
            if (!Array.isArray(rifas) || rifas.length === 0) {
                mostrarGraficoEjemplo();
                return;
            }

            const numerosReservados = Array.isArray(numeros) ? 
                numeros.filter(numero => numero && numero.estado === 'reservado') : [];
            const numerosPorRifa = {};

        // Agrupar números reservados por idRifa
        numerosReservados.forEach(numero => {
            if (numero && numero.idRifa && typeof numero.idRifa !== 'undefined') {
                const { idRifa } = numero;
                if (!numerosPorRifa[idRifa]) {
                    numerosPorRifa[idRifa] = { totalNumeros: 0, reservados: 1 }; // Inicia con reservados = 1
                } else {
                    numerosPorRifa[idRifa].reservados += 1;
                }
            }
        });

        // Calcular el total de números por rifa
        if (Array.isArray(numeros)) {
            numeros.forEach(numero => {
                if (numero && numero.idRifa && typeof numero.idRifa !== 'undefined') {
                    const { idRifa } = numero;
                    if (!numerosPorRifa[idRifa]) {
                        numerosPorRifa[idRifa] = { totalNumeros: 1, reservados: 0 };
                    } else {
                        numerosPorRifa[idRifa].totalNumeros += 1;
                    }
                }
            });
        }

        // Ordenar rifas por cantidad de números reservados
        const rifasOrdenadas = Object.entries(numerosPorRifa)
            .filter(([idRifa, data]) => idRifa && data && typeof data.reservados === 'number')
            .sort(([, a], [, b]) => (b.reservados || 0) - (a.reservados || 0))
            .slice(0, 6);

        const labels = rifasOrdenadas.map(([idRifa, { totalNumeros, reservados }]) => {
            const rifa = Array.isArray(rifas) ? rifas.find(r => r && r.idRifa && String(r.idRifa) === String(idRifa)) : null;
            const rifaNombre = rifa && rifa.nombre ? rifa.nombre.slice(0, 15) : `Rifa ${idRifa}`; // Limitar a 15 caracteres
            return `${rifaNombre}: Reservados: ${reservados || 0}`;
        });

        const data = rifasOrdenadas.map(([, { reservados }]) => reservados || 0);

        const backgroundColors = generateColorShades('#181442', labels.length);

        setChartData({
            labels: labels,
            datasets: [
                {
                    label: 'Números reservados por rifa',
                    data: data,
                    backgroundColor: backgroundColors,
                    borderColor: '#181442',
                    borderWidth: 1,
                },
            ],
        });

        setChartOptions({
            plugins: {
                legend: {
                    labels: {
                        color: '#333',
                    },
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    grid: {
                        color: '#ccc',
                    },
                },
            },
        });
        } catch (error) {
            console.error('Error processing chart data:', error);
            mostrarGraficoEjemplo();
        }
    };

    // Mostrar gráfico de ejemplo
    const mostrarGraficoEjemplo = () => {
        const backgroundColors = generateColorShades('#181442', 3);

        setChartData({
            labels: ['Ejemplo 1', 'Ejemplo 2', 'Ejemplo 3'],
            datasets: [
                {
                    label: 'Gráfico de Ejemplo',
                    data: [10, 15, 20],
                    backgroundColor: backgroundColors,
                    borderColor: '#181442',
                    borderWidth: 1,
                },
            ],
        });

        setChartOptions({
            plugins: {
                legend: {
                    labels: {
                        color: '#333',
                    },
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    grid: {
                        color: '#ccc',
                    },
                },
            },
        });
    };

    // Generar tonos de color
    const generateColorShades = (baseColor, count) => {
        const r = parseInt(baseColor.slice(1, 3), 16);
        const g = parseInt(baseColor.slice(3, 5), 16);
        const b = parseInt(baseColor.slice(5, 7), 16);

        const shades = [];
        for (let i = 0; i < count; i++) {
            const alpha = 0.7 - i * 0.1;
            shades.push(`rgba(${r}, ${g}, ${b}, ${alpha})`);
        }
        return shades;
    };

    // Llamamos a procesarDatosGrafico solo cuando los datos estén cargados
    useEffect(() => {
        procesarDatosGrafico();
    }, [rifas, numeros]);

    return (
        <div className="GraficoContent">
            <h3 className="titleGrafico">Números reservados por rifa</h3>
            <div className="grafico-container-2">
                {rifasLoading || numerosLoading ? (
                    <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        color: '#666',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                    }}>
                        Cargando datos...
                    </div>
                ) : rifasError || numerosError ? (
                    <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        color: '#dc3545',
                        backgroundColor: '#f8d7da',
                        borderRadius: '8px'
                    }}>
                        Error al cargar los datos
                    </div>
                ) : chartData && chartData.datasets && chartData.datasets.length > 0 ? (
                    <PolarArea data={chartData} options={chartOptions} />
                ) : (
                    <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        color: '#666',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                    }}>
                        No hay datos disponibles
                    </div>
                )}
            </div>
        </div>
    );
}
